import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight, BookOpen, Download } from "lucide-react";

export default function Ebook() {
  const [book, setBook] = useState(null);
  const [i, setI] = useState(0); // 0 = cover

  useEffect(() => { api.ebook().then(setBook).catch(() => {}); }, []);
  if (!book) return <div className="max-w-2xl mx-auto px-5 py-24 text-center text-[#918780]">Opening the book…</div>;

  const totalViews = book.pages.length + 1;
  const isCover = i === 0;
  const page = !isCover ? book.pages[i - 1] : null;

  return (
    <div className="max-w-2xl mx-auto px-5 py-10">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-5 text-sm text-[#918780]">
        <span className="flex items-center gap-2 font-semibold"><BookOpen size={16} /> <span className="hidden sm:inline">The RelateMetrics eBook</span><span className="sm:hidden">eBook</span></span>
        <div className="flex items-center gap-3 sm:gap-4">
          <a href={api.ebookPdf()} target="_blank" rel="noreferrer" data-testid="ebook-download-pdf"
            className="inline-flex items-center gap-1.5 text-[#6B21A8] hover:text-[#4C1D95] font-semibold transition-colors">
            <Download size={15} /> Download PDF
          </a>
          <span data-testid="ebook-progress">{isCover ? "Cover" : `Page ${page.n} of ${book.pages.length}`}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.3 }}
          className="bg-white border border-[#E8E1D9] rounded-2xl p-8 sm:p-12 shadow-sm min-h-[440px]"
        >
          {isCover ? (
            <div className="text-center py-10">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#6B21A8]">A RelateMetrics eBook</span>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2625] mt-4 leading-tight">{book.title}</h1>
              <div className="w-16 h-1 bg-[#6B21A8] rounded-full mx-auto my-6" />
              <p className="text-[#6E655F] text-lg leading-relaxed max-w-md mx-auto">{book.subtitle}</p>
            </div>
          ) : (
            <div>
              <div className="text-sm font-semibold text-[#6B21A8] mb-2">Chapter {page.n}</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2B2625] mb-5 leading-snug">{page.title}</h2>
              <div className="space-y-4">
                {page.body.map((para, idx) => (
                  <p key={idx} className="text-[#35313A] leading-[1.75] text-[16px]">{para}</p>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-6">
        <button data-testid="ebook-prev" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0}
          className="inline-flex items-center gap-1.5 text-[#6E655F] hover:text-[#6B21A8] font-medium disabled:opacity-40">
          <ChevronLeft size={18} /> Previous
        </button>
        <div className="flex-1 mx-6 h-1.5 rounded-full bg-[#E8E1D9] overflow-hidden">
          <div className="h-full bg-[#6B21A8] rounded-full transition-all duration-300" style={{ width: `${(i / (totalViews - 1)) * 100}%` }} />
        </div>
        <button data-testid="ebook-next" onClick={() => setI((v) => Math.min(totalViews - 1, v + 1))} disabled={i === totalViews - 1}
          className="inline-flex items-center gap-1.5 text-[#6E655F] hover:text-[#6B21A8] font-medium disabled:opacity-40">
          Next <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
